import { ReactNode, useState } from "react";
import { useDispatch } from "react-redux";
import { useOutletContext, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from "@headlessui/react";
import { setModalxComponent, setModalxComponentProps } from "components/modalx/modalx.slice";
import LoadFailed from "./load-failed";
import ProductHeader from "./product-header";
import ProductSalesTab, { SalesPeriod } from "./product-sales-tab";
import { MetaTable, ReservationsTable, StockTable } from "./product-tables";
import type { ProductsOutletContext } from "./products";
import { useProductDetails, useVatPercents } from "./use-product-details";

interface ProductTab {
  id: string;
  label: string;
  count?: number;
  content: ReactNode;
}

/** The right side of the products page: the product named in the address. */
export default function ProductDetail() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { companyId = "", productId = "" } = useParams();
  const { reloadList } = useOutletContext<ProductsOutletContext>();
  const { product, isFailed, reload } = useProductDetails(companyId, productId);
  const vatPercents = useVatPercents();
  // Kept here, not in the sales tab, so it survives moving to another product.
  const [period, setPeriod] = useState<SalesPeriod>("thisYear");
  const [today] = useState(() => new Date());

  const handleEdit = () => {
    dispatch(setModalxComponent("ProductForm"));
    dispatch(
      setModalxComponentProps({
        id: productId,
        onSubmit: () => {
          reload();
          reloadList();
        },
      })
    );
  };

  if (isFailed) {
    return (
      <div className="p-8">
        <LoadFailed text={t("products_page_load_failed")} onRetry={reload} />
      </div>
    );
  }
  if (!product) return <DetailSkeleton />;

  // The tabs, left to right. Stock and reservations belong to goods only; Finvalda fields show when the product has some.
  const tabs: ProductTab[] = [
    {
      id: "sales",
      label: t("products_page_tab_sales"),
      content: (
        <ProductSalesTab companyId={companyId} productId={product.id} period={period} onPeriodChange={setPeriod} today={today} />
      ),
    },
  ];
  if (product.type === "product") {
    tabs.push(
      { id: "stock", label: t("products_page_tab_stock"), count: product.stock.length, content: <StockTable stock={product.stock} /> },
      {
        id: "reservations",
        label: t("products_page_tab_reservations"),
        count: product.reservations.length,
        content: <ReservationsTable reservations={product.reservations} />,
      }
    );
  }
  if (Object.keys(product.meta || {}).length > 0) {
    tabs.push({ id: "meta", label: t("products_page_tab_meta"), content: <MetaTable meta={product.meta} /> });
  }

  // The product endpoint sends the rate id; ProductForm also accepts a populated rate, so this does too.
  const vatId = product.vat && typeof product.vat === "object" ? (product.vat as { id: string }).id : product.vat;

  return (
    <div className="mx-auto max-w-5xl p-4 lg:p-8">
      <ProductHeader product={product} vatPercent={vatId ? vatPercents[vatId] : undefined} onEdit={handleEdit} />

      {/* A new product, or an edit that changes which tabs exist, starts on the first tab. */}
      <TabGroup key={`${product.id}:${tabs.map((tab) => tab.id).join(",")}`}>
        <TabList className="mt-6 flex gap-6 overflow-x-auto border-b border-gray-200 text-sm">
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              className="-mb-px whitespace-nowrap border-b-2 border-transparent pb-2.5 font-medium text-gray-500 outline-none hover:text-gray-800 data-[selected]:border-indigo-600 data-[selected]:text-indigo-700"
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-1 rounded-full bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{tab.count}</span>
              )}
            </Tab>
          ))}
        </TabList>
        <TabPanels className="mt-4">
          {tabs.map((tab) => (
            <TabPanel key={tab.id}>{tab.content}</TabPanel>
          ))}
        </TabPanels>
      </TabGroup>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="mx-auto max-w-5xl animate-pulse p-4 lg:p-8" data-testid="product-detail-skeleton">
      <div className="h-40 rounded-xl bg-gray-100" />
      <div className="mt-6 h-48 rounded-xl bg-gray-100" />
    </div>
  );
}
